import React, {useCallback, useState} from 'react';

import {Analytics} from '@buncha/config/analytics/analytics';
import AppModal from '@buncha/common/components/CustomModal/AppModal';
import {CommonScreens} from '@buncha/common/navigation/types';
import {CustomText} from '@buncha/components/core/text';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {MainScreens} from '@buncha/navigation/types';
import {Modules} from '@buncha/utils/types';
import {PrinterNativeModule} from '../../PrinterNativeModule';
import {ToastType} from '@buncha/components/composites/notification/type';
import {Touchable} from '@buncha/components/core/touchable';
import {navigation} from '@buncha/config/navigation';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';
import {useGenerateBarcodesFile} from '@buncha/driver/hooks/printScanBarcodes';
import {useGetLocale} from '@buncha/localization/hooks';
import {useToastMessage} from '@buncha/components/composites/notification';

interface ReprintModalProps {
  open: boolean;
  onClose: () => void;
  orderIds: number[];
  b2BOrderIds: number[];
  barcodesLength: number;
}

const ReprintModal: React.FC<ReprintModalProps> = props => {
  const {generateBarcodesFile, loading} = useGenerateBarcodesFile();
  const [showSuccessToast] = useToastMessage(ToastType.Success);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const [printingLabel, setPrintingLabel] = useState(false);
  const [hasError, setHasError] = useState(false);
  const BLEDevice = useAppSelector(gbState => gbState.driver.BLEDevice);
  const {body, button} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};

  const rnPrinter = PrinterNativeModule.RNPrinter;
  const reprintBarcodes = useCallback(async () => {
    const orderIds = props?.orderIds ?? [];
    const b2BOrderIds = props?.b2BOrderIds ?? [];
    if (orderIds.length || b2BOrderIds.length) {
      const barcodeData = await generateBarcodesFile(orderIds, b2BOrderIds);
      if (barcodeData && BLEDevice?.address) {
        setPrintingLabel(true);
        setHasError(false);
        // await rnPrinter.ScanDevice(2)
        rnPrinter
          .PrintLabel(barcodeData, BLEDevice?.address)
          .then(() => {
            showSuccessToast('Printing completed successfully');
            Analytics.eventWithProps('Printer scan', {
              status: 'success',
            });
            setPrintingLabel(false);
            props.onClose();
          })
          .catch(error => {
            setHasError(true);
            // eslint-disable-next-line no-console
            console.log('Error= >>>>', error);
            Analytics.eventWithProps('Printer scan', {
              status: 'failed',
              error: error?.message ?? '',
            });
            setPrintingLabel(false);
            showErrorToast(
              "Oops! Looks like there's an issue. Could you please double-check your printer settings?",
            );
          });
      }
    }
  }, [
    BLEDevice?.address,
    generateBarcodesFile,
    props,
    rnPrinter,
    showErrorToast,
    showSuccessToast,
  ]);

  const theme = useColorThemeType('main');
  const navigateToSetting = useCallback(() => {
    props?.onClose();
    navigation.navigate(MainScreens.Common, {
      screen: CommonScreens.PrinterSetting,
    });
  }, [props]);

  const title = body?.reprintConfimation?.replace(
    '{barcodesLength}',
    props?.barcodesLength,
  );
  return (
    <AppModal
      title={body?.printBarcodes}
      loading={loading || printingLabel}
      subTitle={title}
      disableCtaTop={loading || printingLabel}
      ctaTopTitle={button?.reprintBarcodes}
      ctaBottomTitle={button?.cancel}
      topCTAColor={theme?.background?.green?.[100]}
      isOpen={props?.open}
      onClose={props?.onClose}
      topCTAHandelr={reprintBarcodes}
      bottomCTAHandelr={props?.onClose}
      bottomCTA={
        <If condition={hasError}>
          <Touchable onPress={navigateToSetting} style={{marginTop: 20}}>
            <CustomText style={{color: theme?.text.red[100]}}>
              {button?.updatePrinter}
            </CustomText>
          </Touchable>
        </If>
      }
    />
  );
};

export default React.memo(ReprintModal);
